import { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Boxes, Box, AlertTriangle, ShieldAlert } from 'lucide-react'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { useStackRecoveryStats, useRequestStackRecovery, useConversionOptions, useRedObjects } from '../hooks'
import { ScanFirstOption } from './ScanFirstOption'
import { ConversionFormatPicker, buildConvertFormats, type ConversionMode } from './ConversionFormatPicker'
import { RedObjectChoicePicker, type RedChoice } from './RedObjectChoicePicker'
import { useQuery } from '@tanstack/react-query'
import { classificationsApi } from '@/features/classifications/api'
import { ClassificationSlider } from './ClassificationSlider'
import { RedObjectsListDialog } from './RedObjectsListDialog'
import { formatBytes } from '../utils/formatSize'

interface RequestRecoveryDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    objectID: string | null
    scenario: number | null
    /** Told the new request's id when the success message is closed (the impact assessment then groups it). */
    onRequested?: (requestId: string) => void
}

export function RequestRecoveryDialog({
    open,
    onOpenChange,
    objectID,
    scenario,
    onRequested,
}: RequestRecoveryDialogProps) {
    const { t } = useTranslation()
    // The band the user picked; null until they pick one. With no band sent, the server answers for
    // the stack's own lowest band, so the first answer is already the default. This used to start
    // at 1 and then copy that answer back into the query key, which re-ran the same four
    // MainMapping reads for an identical result on every open.
    const [chosenBand, setChosenBand] = useState<number | null>(null)
    const [madeRequestId, setMadeRequestId] = useState<string | null>(null)
    // Gated on `open` like the other two queries below. The Object page keeps this dialog mounted
    // while it is closed, so ungated it queried the stack on every page load.
    const { data: stats, isLoading: isLoadingStats } = useStackRecoveryStats(open ? objectID : null, chosenBand ?? undefined)
    const minSourceLayer = chosenBand ?? stats?.minSourceLayer ?? null
    // Returns the ARRAY, not { data: array }. This shares the ['classifications']
    // cache key with the dependency canvas, NodeDetails and AddNodePanel, all of
    // which return the array — and React Query caches by key, not by caller. When
    // this ran first it poisoned the shared entry with an object, and the canvas's
    // `for (const c of allClassifications || [])` threw "is not iterable", taking
    // /visualization down behind an ErrorBoundary. `|| []` cannot rescue a value
    // that is truthy but not iterable.
    const { data: layersResponse } = useQuery({
        queryKey: ['classifications'],
        queryFn: async () => {
            const r = await classificationsApi.list()
            return ((r.data as any).data || []) as any[]
        },
    })
    const requestMutation = useRequestStackRecovery()
    const [scanFirst, setScanFirst] = useState(false)
    const { data: redObjects = [] } = useRedObjects(open ? objectID : null)
    // Scoped on minSourceLayer as well as the object: moving the classification band changes which
    // objects the request will contain, so it has to change what conversion is offered for.
    // No band sent = the whole stack, which is the same scope as its lowest band.
    const { data: conversionData, isLoading: isLoadingConversion } = useConversionOptions(
        objectID,
        { mode: 'stack', scenario: scenario ?? 1, minSourceLayer: chosenBand ?? undefined },
        open,
    )
    const [recoverRedObjects, setRecoverRedObjects] = useState<'Yes' | 'No'>('No')
    const [showRedObjectsDialog, setShowRedObjectsDialog] = useState(false)
    const [showSuccessDialog, setShowSuccessDialog] = useState(false)
    // Egress conversion. Default 'none' — raw is what the vault holds and what our restore agent
    // uses, so converting is the exception the customer opts into, never the assumption.
    const [conversionMode, setConversionMode] = useState<ConversionMode>('none')
    const [conversionAllTargets, setConversionAllTargets] = useState<string[]>([])
    const [conversionPerObject, setConversionPerObject] = useState<Record<string, string[]>>({})
    // Per-object override of the red/green answer. Empty = every object follows the request-wide
    // choice above, which is exactly today's behaviour.
    const [redChoices, setRedChoices] = useState<Record<string, RedChoice>>({})
    // Objects the request could not include. Previously these vanished silently.
    const [excluded, setExcluded] = useState<Array<{ objectId: string; reason: string }>>([])
    const [showErrorDialog, setShowErrorDialog] = useState(false)
    const [recoveryErrorMessage, setRecoveryErrorMessage] = useState<string | null>(null)

    // Bounds of the stack, declared BEFORE the memo below that reads them.
    // They used to sit further down the component: a const is in the temporal
    // dead zone until its declaration runs, so the dependency array evaluated
    // at render threw "Cannot access before initialization" and took the whole
    // Object page down with it.
    // Both bounds are Classifications.DisplayRank, which the API derives from
    // the object's classification — NOT the retired 0..22 LayerID ladder. The
    // old `|| 10` / `|| 1` defaults were ladder-scale literals: before the
    // stats arrived they admitted only DisplayRank <= 10, i.e. 3 of the 44 live
    // classifications. Until the real bounds are known there is no stack to
    // bound, so offer nothing rather than a wrong slice; Confirm is already
    // disabled while isLoadingStats.
    const maxLayer = stats?.maxLayerID ?? null
    const minLayer = stats?.minSourceLayer ?? null

    // Classification bands in range for this stack, for the slider — which draws them in stack
    // order, highest at the top, and puts the category where it changes. The recovery filter is
    // "recover everything from this classification upward", and a vertical column in stack order
    // shows that cut; the dropdown this replaced listed 44 names and showed nothing of what
    // "upward" meant (Andrew, 2026-09-21).
    const inRangeClasses = useMemo(() => {
        // Both halves of a merge, and both are load-bearing. The null guard: line below
        // compares Number(c.DisplayRank) >= minLayer, and Number(x) >= null coerces to
        // >= 0, which would admit every classification instead of none while the stats
        // are still loading. The unwrap: the ['classifications'] queryFn now returns the
        // ARRAY itself, not { data }, after the shared-query-key fix — reading .data here
        // again would hand back undefined.
        if (minLayer === null || maxLayer === null) return []
        const all = ((layersResponse as any) || []) as any[]
        return all.filter((c) => Number(c.DisplayRank) >= minLayer && Number(c.DisplayRank) <= maxLayer)
    }, [layersResponse, minLayer, maxLayer])

    const handleConfirm = async () => {
        if (!objectID || !scenario || minSourceLayer === null) return
        setRecoveryErrorMessage(null)

        try {
            const result = await requestMutation.mutateAsync({
                objectID,
                recoverRedObjects,
                scanFirst,
                minSourceLayer,
                scenario,
                convertFormats: buildConvertFormats(
                    conversionMode,
                    conversionData?.objects ?? [],
                    conversionAllTargets,
                    conversionPerObject,
                ),
                // Sent as a proc parameter, not stamped afterwards: it decides which chain the
                // request is built from, so it cannot be corrected once the request exists.
                redObjectChoices: Object.keys(redChoices).length > 0 ? redChoices : undefined,
            })

            if (result?.success) {
                setMadeRequestId(result.requestId ?? null)
                setExcluded(result.excluded ?? [])
                setShowSuccessDialog(true)
            } else {
                const msg =
                    (result as { sqlMessage?: string; outcome?: string })?.sqlMessage ||
                    (result as { sqlMessage?: string; outcome?: string })?.outcome ||
                    t('objects.recoveryRequestErrorMessage')
                setRecoveryErrorMessage(msg)
                setShowErrorDialog(true)
            }
        } catch (error: unknown) {
            const msg =
                (error as { response?: { data?: { data?: { sqlMessage?: string } } } })?.response?.data?.data?.sqlMessage ||
                (error as Error)?.message ||
                t('objects.recoveryRequestErrorMessage')
            setRecoveryErrorMessage(msg)
            setShowErrorDialog(true)
        }
    }

    const handleSuccessClose = () => {
        setShowSuccessDialog(false)
        onOpenChange(false)
        if (madeRequestId && onRequested) onRequested(madeRequestId)
        setMadeRequestId(null)
    }

    if (!objectID || !scenario) {
        return null
    }

    return (
        <>
            <Dialog open={open && !showSuccessDialog && !showErrorDialog} onOpenChange={onOpenChange}>
                {/* Two columns (Andrew, 2026-09-21). The classification slider is tall and narrow and
                    everything else is wide and short, so side by side the dialog is only as tall as the
                    slider needs, and Confirm no longer ends up below the bottom of the screen. The
                    header and footer are pinned; on md and up the left column scrolls on its own
                    (the per-object conversion list can run long) and the right column is the slider
                    at the full height of the body. Below md it stacks and the body scrolls between
                    the pinned header and footer. */}
                <DialogContent className="flex max-h-[92vh] flex-col gap-0 overflow-hidden p-0 sm:max-w-[980px]">
                    <DialogHeader className="shrink-0 px-6 pb-4 pt-6">
                        <DialogTitle>{t('objects.requestRecovery')}</DialogTitle>
                        <DialogDescription>
                            {t('objects.requestRecoveryDescription', { objectID: objectID || '' })}
                        </DialogDescription>
                    </DialogHeader>

                    <div className="grid min-h-0 flex-auto gap-6 overflow-y-auto px-6 pb-4 md:grid-cols-[minmax(0,1.35fr)_minmax(0,1fr)] md:grid-rows-[minmax(0,1fr)] md:overflow-hidden">
                        {/* What the stack holds, red or green, and conversion. */}
                        <div className="min-h-0 space-y-5 md:overflow-y-auto md:pr-2">
                            {/* The three figures, compact: three across in the narrower column. */}
                            <div className="grid grid-cols-3 gap-3">
                                <div className="rounded-lg border bg-card p-3">
                                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                                        <Boxes className="h-4 w-4 shrink-0" />
                                        <span className="truncate">{t('objects.numberOfServices')}</span>
                                    </div>
                                    <p className="mt-1 text-xl font-bold">
                                        {isLoadingStats ? '-' : (stats?.servicesCount || 0)}
                                    </p>
                                </div>
                                <div className="rounded-lg border bg-card p-3">
                                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                                        <Box className="h-4 w-4 shrink-0" />
                                        <span className="truncate">{t('objects.numberOfObjects')}</span>
                                    </div>
                                    <p className="mt-1 text-xl font-bold">
                                        {isLoadingStats ? '-' : (stats?.objectsCount || 0)}
                                    </p>
                                </div>
                                <div className="rounded-lg border bg-card p-3">
                                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                                        <AlertTriangle className="h-4 w-4 shrink-0" />
                                        <span className="truncate">{t('objects.redObjects')}</span>
                                    </div>
                                    <div className="mt-1 flex items-baseline justify-between gap-2">
                                        <p className="text-xl font-bold text-red-600">
                                            {isLoadingStats ? '-' : (stats?.redObjectsCount || 0)}
                                        </p>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            className="h-6 px-1.5 text-xs"
                                            onClick={() => setShowRedObjectsDialog(true)}
                                            disabled={!stats?.redObjectsCount || stats.redObjectsCount === 0}
                                        >
                                            {t('objects.listRedObjects')}
                                        </Button>
                                    </div>
                                </div>
                            </div>

                            {/* Recover Red or Green Objects */}
                            <div className="space-y-2">
                                <Label>{t('objects.recoverObjects')}</Label>
                                <div className="flex flex-wrap gap-3">
                                    <Button
                                        type="button"
                                        variant={recoverRedObjects === 'Yes' ? 'default' : 'outline'}
                                        size="sm"
                                        onClick={() => setRecoverRedObjects('Yes')}
                                        className={recoverRedObjects === 'Yes' ? 'bg-red-600 hover:bg-red-700 text-white' : ''}
                                    >
                                        {t('objects.retrieveRedObjects')}
                                    </Button>
                                    <Button
                                        type="button"
                                        variant={recoverRedObjects === 'No' ? 'default' : 'outline'}
                                        size="sm"
                                        onClick={() => setRecoverRedObjects('No')}
                                        className={recoverRedObjects === 'No' ? 'bg-green-600 hover:bg-green-700 text-white' : ''}
                                    >
                                        {t('objects.retrieveGreenObjects')}
                                    </Button>
                                </div>
                            </div>

                            {/* Per-object override. Renders nothing when no copy is compromised. */}
                            <RedObjectChoicePicker
                                redObjects={redObjects}
                                globalChoice={recoverRedObjects}
                                choices={redChoices}
                                onChange={setRedChoices}
                            />

                            {/* Convert disk images on egress. Only ever offered for objects that
                                actually carry a readable disk image — see ConversionFormatPicker. */}
                            <ConversionFormatPicker
                                options={conversionData?.objects ?? []}
                                isLoading={isLoadingConversion}
                                mode={conversionMode}
                                onModeChange={setConversionMode}
                                allTargets={conversionAllTargets}
                                onAllTargetsChange={setConversionAllTargets}
                                perObject={conversionPerObject}
                                onPerObjectChange={setConversionPerObject}
                            />
                        </div>

                        {/* The stack itself: recover this classification and everything above it. The
                            minimum keeps 40-odd rows workable when the left column is short; it is
                            capped by the viewport so a short window does not clip the column. */}
                        <div className="flex min-h-[380px] flex-col gap-2 md:min-h-[min(440px,55vh)]">
                            <Label id="recover-from-classification">{t('objects.minClassification', 'Recover from classification')}</Label>
                            <div className="min-h-0 flex-1">
                                <ClassificationSlider
                                    classes={inRangeClasses}
                                    value={minSourceLayer}
                                    onChange={setChosenBand}
                                    labelledBy="recover-from-classification"
                                    disabled={isLoadingStats}
                                    height="fill"
                                />
                            </div>
                            <p className="text-xs text-muted-foreground">
                                {t('objects.minClassificationHelp', 'Everything at this classification and above it in the stack is recovered.')}
                            </p>
                        </div>
                    </div>

                    <div className="shrink-0 px-6 pb-3 empty:hidden">
                        <ScanFirstOption checked={scanFirst} onChange={setScanFirst} disabled={requestMutation.isPending} />
                    </div>

                    <DialogFooter className="shrink-0 border-t px-6 py-4">
                        <Button variant="outline" onClick={() => onOpenChange(false)} disabled={requestMutation.isPending}>
                            {t('common.cancel')}
                        </Button>
                        <Button
                            onClick={handleConfirm}
                            disabled={requestMutation.isPending || isLoadingStats || minSourceLayer === null}
                        >
                            {requestMutation.isPending
                                ? t('common.processing')
                                : t('objects.confirmRequestRecovery')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Red Objects List Dialog */}
            <RedObjectsListDialog
                open={showRedObjectsDialog}
                onOpenChange={setShowRedObjectsDialog}
                objectID={objectID}
            />

            {/* Success Dialog */}
            <Dialog open={showSuccessDialog} onOpenChange={(next) => (!next && onRequested ? handleSuccessClose() : setShowSuccessDialog(next))}>
                <DialogContent className="sm:max-w-[500px]">
                    <DialogHeader>
                        <DialogTitle>{t('objects.recoveryRequested')}</DialogTitle>
                        <DialogDescription>
                            {t('objects.recoveryRequestedMessage', { objectID })}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3 py-4">
                        <p className="text-sm text-muted-foreground">
                            {t('objects.recoveryNeedsApproval')}
                        </p>
                        {/* An object with no usable copy is left out of the request entirely. It
                            used to disappear without a word — the request reported success and the
                            server was simply absent when the recovery ran. */}
                        {excluded.length > 0 && (
                            <div className="rounded-md border border-amber-500/40 bg-amber-500/5 p-3">
                                <p className="flex items-center gap-2 text-sm font-medium text-amber-700 dark:text-amber-500">
                                    <ShieldAlert className="h-4 w-4 shrink-0" />
                                    {t('objects.excludedTitle', {
                                        count: excluded.length,
                                        defaultValue: '{{count}} object(s) could not be included',
                                    })}
                                </p>
                                <ul className="mt-2 space-y-1">
                                    {excluded.map((e) => (
                                        <li key={e.objectId} className="text-xs text-muted-foreground">
                                            <span className="font-medium">{e.objectId}</span>{' — '}
                                            {e.reason === 'clean-copy-requested-none-exists'
                                                ? t('objects.excludedNoCleanRequested', 'the last clean copy was chosen, but there is no clean copy in the vault')
                                                : t('objects.excludedNoClean', 'every copy in the vault is compromised, and the compromised copy was declined')}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                    <DialogFooter>
                        <Button onClick={handleSuccessClose}>
                            {t('common.yes')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Error Dialog */}
            <Dialog open={showErrorDialog} onOpenChange={(open) => { setShowErrorDialog(open); if (!open) setRecoveryErrorMessage(null) }}>
                <DialogContent className="sm:max-w-[500px]">
                    <DialogHeader>
                        <DialogTitle>{t('objects.recoveryRequestError')}</DialogTitle>
                        <DialogDescription>
                            {recoveryErrorMessage || t('objects.recoveryRequestErrorMessage')}
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button onClick={() => { setShowErrorDialog(false); setRecoveryErrorMessage(null) }}>
                            {t('common.close')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    )
}
